/**
 * EmptyState — reusable polished empty-state component.
 *
 * Replaces ad-hoc "No X yet" plain Typography blocks across the app. Use
 * for any list-style page (Documents, Tasks, RFIs, Transmittals, etc.)
 * when the filtered or unfiltered result set is empty.
 *
 * Use:
 *   <EmptyState
 *     icon={InboxOutlined}
 *     title="No documents yet"
 *     description="Upload your first drawing package to get started."
 *     action={{ label: "Upload Document", onClick: () => nav('/documents/upload') }}
 *   />
 *
 *   // Or with icon as a JSX node:
 *   <EmptyState icon={<HomeRounded fontSize="large" />} title="..." />
 *
 * Audit-day defense: consistent empty-state UX across every list page
 * means an auditor walking from /documents to /tasks to /rfis sees the
 * same visual pattern. Reduces cognitive load + signals product polish.
 */

import React from "react";
import { Box, Typography, Button, Paper, Stack } from "@mui/material";
import { InboxOutlined } from "@mui/icons-material";

export default function EmptyState({
  icon,
  title = "Nothing here yet",
  description = null,
  action = null,        // { label, onClick, variant?, color? }
  variant = "paper",    // "paper" | "plain"
  size = "md",          // "sm" | "md" | "lg"
}) {
  const padding = { sm: 3, md: 5, lg: 7 }[size] || 5;
  const iconSize = { sm: 32, md: 48, lg: 64 }[size] || 48;

  // Accept either a component ref (e.g. InboxOutlined) or a JSX node
  let iconNode;
  if (icon) {
    if (typeof icon === "function" || (typeof icon === "object" && icon.$$typeof)) {
      // Component ref — render it
      const IconCmp = icon;
      iconNode = <IconCmp sx={{ fontSize: iconSize, color: "text.disabled" }} />;
    } else if (React.isValidElement(icon)) {
      iconNode = icon;
    }
  } else {
    iconNode = <InboxOutlined sx={{ fontSize: iconSize, color: "text.disabled" }} />;
  }

  const inner = (
    <Stack alignItems="center" spacing={1.5} sx={{ textAlign: "center", py: padding, px: 2 }}>
      <Box sx={{ mb: 0.5 }}>{iconNode}</Box>
      <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", maxWidth: 480, lineHeight: 1.6 }}
        >
          {description}
        </Typography>
      )}
      {action && (
        <Button
          onClick={action.onClick}
          variant={action.variant || "contained"}
          color={action.color || "primary"}
          sx={{
            mt: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            letterSpacing: "0.01em",
          }}
        >
          {action.label}
        </Button>
      )}
    </Stack>
  );

  if (variant === "plain") return inner;
  return <Paper variant="outlined" sx={{ borderStyle: "dashed", bgcolor: "transparent" }}>{inner}</Paper>;
}
